import { useContext } from 'react'

import {
  Box, Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { deepSetObject, digObject, stringToKey } from '@domain-group/javascript-utils'

import useCustomField, { useCustomFieldForm } from '@hooks/useCustomField'
import type { UseCustomFieldPayload } from '@hooks/useCustomField'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import type { CustomFieldModel } from '@models/types'
import FieldOptionValues from './components/FieldOptionValues'
import DefaultValue from './components/DefaultValue'

const MODAL_KEY = 'CreateOrEditCustomFieldModal'

type ModalCallbacks = {
  closeModal: () => void,
  createCustomField: (payload: HandleCallbackActionParams) => void,
  updateCustomField: (payload: HandleCallbackActionParams) => void,
}

type CreateOrUpdateCustomFieldParams = {
  callbacks: ModalCallbacks,
  createFn: UseCustomFieldPayload['callbacks']['createCustomField'],
  entityState: Partial<CustomFieldModel>,
  updateFn: UseCustomFieldPayload['callbacks']['updateCustomField'],
}

const createOrUpdateCustomField = (params: CreateOrUpdateCustomFieldParams) => {
  const {
    callbacks: { closeModal, createCustomField, updateCustomField },
    createFn,
    entityState,
    updateFn,
  } = params

  const [actionFn, actionText] = entityState.id ? [updateFn, 'Updated'] : [createFn, 'Created']

  const customFieldPayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Field ${actionText}`,
  }

  if (entityState.id) return updateCustomField(customFieldPayload)

  return createCustomField(customFieldPayload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditCustomFieldModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { customField = {} } = modalPayload

  const newRecord = !customField.id

  const customFieldPayload = useCustomField(customField)
  const {
    callbacks: { createCustomField: createFn, updateCustomField: updateFn },
    creating,
    updating,
  } = customFieldPayload

  const formPayload = useCustomFieldForm(customField, { validateOn: 'change' })
  const {
    entityState,
    entityState: {
      editable,
      field_type,
      hint,
      label,
      key,
      options,
      options: {
        export: {
          reaxml: {
            path: exportReaxmlPath,
          },
        },
        import: {
          reaxml: {
            path: importReaxmlPath,
            path_type: importReaxmlPathType,
          },
        },
        input_type,
        width,
      },
      placeholder,
      required,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Custom Field" />

      <SidebarModal.Content>
        <Form>
          <Form.Field errorMessage={errors.label} label="* Field Label" marginTop="large">
            <input
              name="label"
              onBlur={e => setEntityState({ key: stringToKey(e.target.value) })}
              onChange={e => setEntityState({ label: e.target.value })}
              placeholder="e.g Bedrooms"
              type="text"
              value={label}
            />
          </Form.Field>

          <Form.Field errorMessage={errors.key} label="* Field Key" marginTop="large">
            <input name="key" value={key} placeholder="e.g bedrooms" type="text" {...handlers} />
          </Form.Field>

          <Form.Field marginTop="large" label="Hint">
            <input name="hint" type="text" value={hint || ''} {...handlers} />
          </Form.Field>

          <Form.Field marginTop="large" label="Placeholder">
            <input name="placeholder" type="text" value={placeholder || ''} {...handlers} />
          </Form.Field>

          <Form.Field label="Required" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="required" value={required || 'false'} data-value-type="boolean" {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field label="Editable" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="editable" value={editable ?? 'true'} data-value-type="boolean" {...handlers}>
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </Form.Field>

          <Form.Field errorMessage={errors.field_type} label="* Field Type" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select
              name="field_type"
              value={field_type}
              {...handlers}
              onChange={e => (
                setEntityState({ field_type: e.target.value, options: deepSetObject(options, 'default_value', '') })
              )}
            >
              <option value="">Please Select...</option>
              <option value="input">Input</option>
              <option value="select">Select</option>
              <option value="textarea">Textarea</option>
            </select>
          </Form.Field>

          {field_type === 'input' && (
            <Form.Field label="Input Type" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <select name="options.input_type" value={input_type} {...handlers}>
                <option value="">Please Select...</option>
                <option value="date">Date</option>
                <option value="email">Email</option>
                <option value="number">Number</option>
                <option value="text">Text</option>
                <option value="url">Url</option>
              </select>
            </Form.Field>
          )}

          <DefaultValue formPayload={formPayload} />

          <Form.Field label="Field Width" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select data-value-type="number" name="options.width" value={width || ''} {...handlers}>
              <option value="100">100%</option>
              <option value="75">75%</option>
              <option value="50">50%</option>
              <option value="25">25%</option>
            </select>
          </Form.Field>
        </Form>

        <SidebarModal.Separator boxProps={{ marginTop: 'large' }} />

        {field_type === 'select' && (
          <>
            <FieldOptionValues callbacks={{ setEntityState }} options={options} />

            <SidebarModal.Separator boxProps={{ marginTop: 'medium' }} />
          </>
        )}

        <Form.Field label="REAXML Import" marginTop="large">
          <Box borderRadius={5} flexDirection="column" padding="large" variant="white">
            <Form.Field label="Import Path Type">
              {/*
               // @ts-expect-error TS2322 */}
              <select name="options.import.reaxml.path_type" value={importReaxmlPathType || ''} {...handlers}>
                <option value="">Please Select...</option>
                <option value="xpath">XPath</option>
              </select>
            </Form.Field>

            <Form.Field label="Import Path" marginTop="large">
              <input name="options.import.reaxml.path" type="text" value={importReaxmlPath || ''} {...handlers} />
            </Form.Field>
          </Box>
        </Form.Field>

        <Form.Field label="REAXML Export" marginTop="large">
          <Box borderRadius={5} flexDirection="column" padding="large" variant="white">
            <Form.Field label="Export Path">
              <input name="options.export.reaxml.path" type="text" value={exportReaxmlPath || ''} {...handlers} />
            </Form.Field>
          </Box>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={() => createOrUpdateCustomField({
            callbacks, createFn, entityState, updateFn,
          })}
          size="large"
        >
          {newRecord ? 'Create Field' : 'Update Field'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditCustomFieldModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
